import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, FileText, Flame, Euro, TrendingUp, Building2, ArrowRight, Sparkles, Activity } from "lucide-react";
import { formatEUR } from "@/lib/constants";
import AgencyPortalCard from "@/components/AgencyPortalCard";
import PortalFunnelCard from "@/components/PortalFunnelCard";
import EmailTestCard from "@/components/EmailTestCard";
import EmailDnsHintCard from "@/components/EmailDnsHintCard";
import AgencyLegalCard from "@/components/AgencyLegalCard";

const KpiTile = ({ icon: Icon, label, value, subtle }) => (
  <Card className="p-5 border-slate-200">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</p>
      <Icon className="w-4 h-4 text-orange-600" />
    </div>
    <p className="text-2xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</p>
    {subtle && <p className="text-[11px] text-slate-400 mt-1">{subtle}</p>}
  </Card>
);

export default function AgencyDashboard() {
  const { agency, checkAuth } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [switching, setSwitching] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/agency/dashboard");
        if (!cancelled) setData(r.data);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openClient = async (cid) => {
    setSwitching(cid);
    try {
      await api.post("/agency/switch-client", { company_id: cid });
      nav("/dashboard");
    } catch (e) {
      setSwitching("");
    }
  };

  if (!data) return <div className="text-slate-500">Wird geladen …</div>;
  const s = data.summary;
  const needsOnboarding = agency && agency.onboarding_completed === false;

  return (
    <div className="space-y-6" data-testid="agency-dashboard-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Agentur-Dashboard</h1>
        <p className="mt-1 text-slate-600">Aggregierte Performance über alle Mandanten Ihrer Agentur.</p>
      </header>

      {needsOnboarding && (
        <Card data-testid="onboarding-hint" className="border-orange-200 bg-orange-50 p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-semibold text-slate-900">Richten Sie Ihren ersten Mandanten ein.</p>
              <p className="text-sm text-slate-600">Mit dem Onboarding-Wizard ist Ihr White-Label-Setup in wenigen Minuten fertig.</p>
            </div>
          </div>
          <Button data-testid="start-onboarding-btn" onClick={() => nav("/agency/onboarding")} className="bg-orange-600 hover:bg-orange-700 text-white">
            Onboarding starten <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Building2} label="Aktive Mandanten" value={`${s.active_clients}/${s.total_clients}`} />
        <KpiTile icon={Users} label="Leads diesen Monat" value={s.leads_this_month} />
        <KpiTile icon={FileText} label="Offene Angebote" value={s.open_offers} />
        <KpiTile icon={Flame} label="Hot Leads" value={s.hot_leads} />
        <KpiTile icon={Euro} label="Gewonnener Umsatz" value={formatEUR(s.won_value)} />
        <KpiTile icon={TrendingUp} label="Geretteter Umsatz" value={formatEUR(s.recovered_revenue)} />
        <KpiTile icon={Activity} label="Reaktiviert (offen)" value={formatEUR(s.reactivated_offer_value)} />
        <KpiTile icon={ArrowRight} label="Antwortquote" value={`${s.avg_response_rate}%`} />
      </div>

      <AgencyPortalCard agency={agency} onRefresh={checkAuth} />

      <PortalFunnelCard />

      <EmailTestCard variant="agency" />
      <EmailDnsHintCard variant="agency" />

      <Card className="border-slate-200" data-testid="clients-performance-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-900">Mandanten-Performance</h2>
          <Button variant="outline" size="sm" onClick={() => nav("/agency/clients")}>Alle Mandanten</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-3">Mandant</th>
                <th className="text-left px-2 py-3">Status</th>
                <th className="text-right px-2 py-3">Leads/Mon</th>
                <th className="text-right px-2 py-3">Offen</th>
                <th className="text-right px-2 py-3">Hot</th>
                <th className="text-right px-2 py-3">Gewonnen</th>
                <th className="text-right px-2 py-3">Gerettet</th>
                <th className="text-right px-2 py-3">Antwort</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.clients.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-8 text-center text-slate-400">Noch keine Mandanten</td></tr>
              ) : data.clients.map(c => (
                <tr key={c.company_id} data-testid={`perf-row-${c.company_id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{c.company_name}</td>
                  <td className="px-2 py-3"><Badge variant={c.status === "active" ? "default" : "outline"} className={c.status === "active" ? "bg-emerald-600 hover:bg-emerald-600" : ""}>{c.status}</Badge></td>
                  <td className="px-2 py-3 text-right tabular-nums">{c.leads_this_month}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{c.open_offers}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{c.hot_leads}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{formatEUR(c.won_value)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{formatEUR(c.recovered_revenue)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{c.response_rate}%</td>
                  <td className="px-5 py-3 text-right">
                    <Button data-testid={`open-client-${c.company_id}`} size="sm" variant="outline" disabled={switching === c.company_id} onClick={() => openClient(c.company_id)}>
                      {switching === c.company_id ? "..." : "Öffnen"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AgencyLegalCard />
    </div>
  );
}
